import { FullFormAction } from "../../../../Engine/Core/Store/FullFormAction";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { UnitStateSelect } from "../../../../Helpers/StateAccessors/UnitStateSelect";
import { currentViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { isUnitIdEquals, UnitId } from "../UnitId/UnitId";
import { UnitIdSet } from "../UnitId/UnitIdImmutableSet";
import { StringArrayKey } from "../../../../../../Common/StringArrayKey";
import { Map2 } from "../../../../../../Common/ModelPath/Map/Map2";

import { RemoveUnitTableWidthActionType, SetUnitTableWidthActionType, UnitActionType } from "./unitActions";

export function unitReducer(state: FullFormState, action: FullFormAction): FullFormState {
    switch (action.type) {
        case UnitActionType.UnitHighlight: {
            if (isUnitIdEquals(action.payload.unitId, state.highlightUnit)) {
                return state;
            }
            const highlightUnitsInfo = state.highlightUnitsInfo ?? new Map2<StringArrayKey, string>();
            if (action.payload.unitId.instance != undefined) {
                const key = new StringArrayKey([
                    action.payload.unitId.pageId,
                    ...(action.payload.unitId.parentInstances ?? []),
                ]);
                highlightUnitsInfo.set(key, action.payload.unitId.instance);
            }
            return { ...state, highlightUnit: action.payload.unitId, highlightUnitsInfo: highlightUnitsInfo };
        }
        case UnitActionType.ShowModalUnit: {
            const { targetUnitId, modalUnitId } = action.payload;
            return {
                ...state,
                modalUnits: [
                    ...state.modalUnits.filter(x => x.targetUnitId.pageId !== targetUnitId.pageId),
                    {
                        targetUnitId: targetUnitId,
                        modalUnitId: modalUnitId,
                    },
                ],
            };
        }
        case UnitActionType.CloseModalUnit: {
            const { targetUnitId } = action.payload;
            const index = state.modalUnits.findIndex(x => isUnitIdEquals(x.targetUnitId, targetUnitId));
            return {
                ...state,
                modalUnits: [...state.modalUnits.slice(0, index), ...state.modalUnits.slice(index + 1)],
            };
        }
        case UnitActionType.ReplaceModalUnit: {
            const { currentModalUnitId, nextModalUnitId } = action.payload;
            const index = state.modalUnits.findIndex(x => isUnitIdEquals(x.modalUnitId, currentModalUnitId));
            const currentModalUnitInfo = state.modalUnits[index];
            if (currentModalUnitInfo == undefined) {
                return state;
            }
            return {
                ...state,
                modalUnits: [
                    ...state.modalUnits.slice(0, index),
                    {
                        ...currentModalUnitInfo,
                        modalUnitId: nextModalUnitId,
                    },
                    ...state.modalUnits.slice(index + 1),
                ],
            };
        }
        case UnitActionType.InitUnitInfo: {
            const { unitInfos } = action.payload;
            return { ...state, unitInfos: unitInfos };
        }
        case UnitActionType.SetUnitTableWidth: {
            return updateUnitWidthByTableWidth(state, action.payload);
        }
        case UnitActionType.RemoveUnitTableWidth: {
            return updateUnitWidthByTableDeletion(state, action.payload);
        }
        case UnitActionType.UpdateEmptyUnits: {
            const { unitId } = action.payload;
            const emptyUnits = action.payload.empty
                ? UnitIdSet.add(state.emptyUnits, unitId)
                : UnitIdSet.remove(state.emptyUnits, unitId);
            return { ...state, emptyUnits: emptyUnits };
        }
        case UnitActionType.AddUnit: {
            const emptyUnits = UnitIdSet.remove(state.emptyUnits, action.payload.unitId);
            return { ...state, emptyUnits: emptyUnits };
        }
        case UnitActionType.RemoveUnit: {
            const { unitId, index, bindingPath } = action.payload;
            let emptyUnits = state.emptyUnits;
            const children = currentViewModelSelect.children(state, bindingPath);

            if (children.length === 0) {
                emptyUnits = UnitIdSet.add(emptyUnits, unitId);
            }
            let nextModalUnits = state.modalUnits;
            const modalUnitIndex = state.modalUnits.findIndex(
                x => x.modalUnitId.pageId === unitId.pageId && x.modalUnitId.instance === index
            );
            if (modalUnitIndex >= 0) {
                nextModalUnits = [
                    ...nextModalUnits.slice(0, modalUnitIndex),
                    ...nextModalUnits.slice(modalUnitIndex + 1),
                ];
            }

            return { ...state, emptyUnits: emptyUnits, modalUnits: nextModalUnits };
        }
        default:
            return state;
    }
}

function updateUnitWidthByTableWidth(
    state: FullFormState,
    { tableId, unitId, width }: SetUnitTableWidthActionType["payload"]
): FullFormState {
    if (UnitStateSelect.getUnitTableWidths(state, unitId)[tableId] === width) {
        return state;
    }

    const updatedState = UnitStateSelect.setUnitTableWidth(state, unitId, tableId, width);

    return recalculateUnitWidthStateByNewState(state, unitId, updatedState);
}

function updateUnitWidthByTableDeletion(
    state: FullFormState,
    { tableId, unitId }: RemoveUnitTableWidthActionType["payload"]
): FullFormState {
    const updatedState = UnitStateSelect.deleteUnitTableWidth(state, unitId, tableId);

    return recalculateUnitWidthStateByNewState(state, unitId, updatedState);
}

function recalculateUnitWidthStateByNewState(
    oldState: FullFormState,
    unitId: UnitId,
    newState: FullFormState
): FullFormState {
    const oldUnitWidth = UnitStateSelect.getUnitWidth(oldState, unitId) || 0;
    const tablesWidthsCollection = UnitStateSelect.getUnitTableWidths(newState, unitId);
    const tablesWidths = Object.values(tablesWidthsCollection);

    const isNoTables = tablesWidths.length === 0;
    if (isNoTables) {
        return UnitStateSelect.deleteUnitWidth(newState, unitId);
    }

    const maxTableWidth = Math.max(...tablesWidths);
    if (maxTableWidth === oldUnitWidth) {
        return newState;
    }

    return UnitStateSelect.setUnitWidth(newState, unitId, maxTableWidth);
}
